import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const AffiliateLandingPage = () => {
  const { t } = useTranslation();

  return (
    <PageContainer>
      <HeroSection>
        <HeroContent>
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {t('affiliate.landing.title')}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            {t('affiliate.landing.subtitle')}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <CTAButton to="/affiliate/signup">
              {t('affiliate.landing.cta.join')}
            </CTAButton>
            <CTAButton to="/affiliate/login" secondary>
              {t('affiliate.landing.cta.login')}
            </CTAButton>
          </motion.div>
        </HeroContent>
      </HeroSection>

      <FeaturesSection>
        <SectionTitle>{t('affiliate.landing.features.title')}</SectionTitle>
        <FeaturesGrid>
          <FeatureCard>
            <FeatureIcon>💰</FeatureIcon>
            <FeatureTitle>{t('affiliate.landing.features.commissions.title')}</FeatureTitle>
            <FeatureDescription>
              {t('affiliate.landing.features.commissions.description')}
            </FeatureDescription>
          </FeatureCard>
          <FeatureCard>
            <FeatureIcon>📈</FeatureIcon>
            <FeatureTitle>{t('affiliate.landing.features.tracking.title')}</FeatureTitle>
            <FeatureDescription>
              {t('affiliate.landing.features.tracking.description')}
            </FeatureDescription>
          </FeatureCard>
          <FeatureCard>
            <FeatureIcon>🔗</FeatureIcon>
            <FeatureTitle>{t('affiliate.landing.features.links.title')}</FeatureTitle>
            <FeatureDescription>
              {t('affiliate.landing.features.links.description')}
            </FeatureDescription>
          </FeatureCard>
          <FeatureCard>
            <FeatureIcon>💎</FeatureIcon>
            <FeatureTitle>{t('affiliate.landing.features.tiers.title')}</FeatureTitle>
            <FeatureDescription>
              {t('affiliate.landing.features.tiers.description')}
            </FeatureDescription>
          </FeatureCard>
        </FeaturesGrid>
      </FeaturesSection>

      <HowItWorksSection>
        <SectionTitle>{t('affiliate.landing.howItWorks.title')}</SectionTitle>
        <StepsContainer>
          <StepCard>
            <StepNumber>1</StepNumber>
            <StepTitle>{t('affiliate.landing.howItWorks.step1.title')}</StepTitle>
            <StepDescription>
              {t('affiliate.landing.howItWorks.step1.description')}
            </StepDescription>
          </StepCard>
          <StepCard>
            <StepNumber>2</StepNumber>
            <StepTitle>{t('affiliate.landing.howItWorks.step2.title')}</StepTitle>
            <StepDescription>
              {t('affiliate.landing.howItWorks.step2.description')}
            </StepDescription>
          </StepCard>
          <StepCard>
            <StepNumber>3</StepNumber>
            <StepTitle>{t('affiliate.landing.howItWorks.step3.title')}</StepTitle>
            <StepDescription>
              {t('affiliate.landing.howItWorks.step3.description')}
            </StepDescription>
          </StepCard>
          <StepCard>
            <StepNumber>4</StepNumber>
            <StepTitle>{t('affiliate.landing.howItWorks.step4.title')}</StepTitle>
            <StepDescription>
              {t('affiliate.landing.howItWorks.step4.description')}
            </StepDescription>
          </StepCard>
        </StepsContainer>
      </HowItWorksSection>

      <FAQSection>
        <SectionTitle>{t('affiliate.landing.faq.title')}</SectionTitle>
        <FAQContainer>
          <FAQItem>
            <FAQQuestion>{t('affiliate.landing.faq.q1')}</FAQQuestion>
            <FAQAnswer>{t('affiliate.landing.faq.a1')}</FAQAnswer>
          </FAQItem>
          <FAQItem>
            <FAQQuestion>{t('affiliate.landing.faq.q2')}</FAQQuestion>
            <FAQAnswer>{t('affiliate.landing.faq.a2')}</FAQAnswer>
          </FAQItem>
          <FAQItem>
            <FAQQuestion>{t('affiliate.landing.faq.q3')}</FAQQuestion>
            <FAQAnswer>{t('affiliate.landing.faq.a3')}</FAQAnswer>
          </FAQItem>
          <FAQItem>
            <FAQQuestion>{t('affiliate.landing.faq.q4')}</FAQQuestion>
            <FAQAnswer>{t('affiliate.landing.faq.a4')}</FAQAnswer>
          </FAQItem>
        </FAQContainer>
      </FAQSection>

      <CTASection>
        <CTAContent>
          <h2>{t('affiliate.landing.finalCta.title')}</h2>
          <p>{t('affiliate.landing.finalCta.description')}</p>
          <CTAButton to="/affiliate/signup">
            {t('affiliate.landing.finalCta.button')}
          </CTAButton>
        </CTAContent>
      </CTASection>
    </PageContainer>
  );
};

// Styled Components
const PageContainer = styled.div`
  max-width: 100%;
  overflow-x: hidden;
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, #6e8efb 0%, #a777e3 100%);
  color: white;
  padding: 100px 20px;
  text-align: center;
`;

const HeroContent = styled.div`
  max-width: 800px;
  margin: 0 auto;

  h1 {
    font-size: 3rem;
    margin-bottom: 20px;
  }

  p {
    font-size: 1.2rem;
    margin-bottom: 40px;
    opacity: 0.9;
  }
`;

const CTAButton = styled(Link)`
  display: inline-block;
  padding: 12px 24px;
  margin: 0 10px;
  background-color: ${props => props.secondary ? 'transparent' : 'white'};
  color: ${props => props.secondary ? 'white' : '#6e8efb'};
  border: 2px solid white;
  border-radius: 30px;
  font-weight: bold;
  text-decoration: none;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  }
`;

const SectionTitle = styled.h2`
  text-align: center;
  font-size: 2.2rem;
  margin-bottom: 50px;
  position: relative;
  color : #504099 !important;

  &:after {
    content: '';
    display: block;
    width: 50px;
    height: 3px;
    background: linear-gradient(90deg, #6e8efb, #a777e3);
    margin: 15px auto 0;
  }
`;

const FeaturesSection = styled.section`
  padding: 80px 20px;
  background-color: #f8f9fa;
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 30px;
  max-width: 1200px;
  margin: 0 auto;
`;

const FeatureCard = styled.div`
  background: white;
  padding: 30px;
  border-radius: 10px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  text-align: center;
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-5px);
  }
`;

const FeatureIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 20px;
`;

const FeatureTitle = styled.h3`
  font-size: 1.4rem;
  margin-bottom: 15px;
  color: #333;
`;

const FeatureDescription = styled.p`
  color: #666;
  line-height: 1.6;
`;

const HowItWorksSection = styled.section`
  padding: 80px 20px;
  background-color: white;
`;

const StepsContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 30px;
`;

const StepCard = styled.div`
  text-align: center;
  padding: 20px;
`;

const StepNumber = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: linear-gradient(135deg, #6e8efb 0%, #a777e3 100%);
  color: white;
  font-size: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
`;

const StepTitle = styled.h3`
  font-size: 1.3rem;
  margin-bottom: 15px;
  color: #333;
`;

const StepDescription = styled.p`
  color: #666;
  line-height: 1.6;
`;

const FAQSection = styled.section`
  padding: 80px 20px;
  background-color: #f8f9fa;
`;

const FAQContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const FAQItem = styled.div`
  margin-bottom: 30px;
  background: white;
  padding: 20px 30px;
  border-radius: 10px;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.05);
`;

const FAQQuestion = styled.h3`
  font-size: 1.2rem;
  margin-bottom: 10px;
  color: #333;
  display: flex;
  justify-content: space-between;
  align-items: center;

  &:after {
    content: '+';
    font-size: 1.5rem;
    color: #6e8efb;
  }
`;

const FAQAnswer = styled.p`
  color: #666;
  line-height: 1.6;
`;

const CTASection = styled.section`
  padding: 80px 20px;
  background: linear-gradient(135deg, #a777e3 0%, #6e8efb 100%);
  color: white;
  text-align: center;
`;

const CTAContent = styled.div`
  max-width: 700px;
  margin: 0 auto;

  h2 {
    font-size: 2.2rem;
    margin-bottom: 20px;
  }

  p {
    font-size: 1.1rem;
    margin-bottom: 30px;
    opacity: 0.9;
  }
`;

export default AffiliateLandingPage;